import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../auth/Session";
import { ErreurApi } from "../api/client";

/**
 * §5.A — La connexion au portail client. C'est désormais la SEULE voie d'entrée.
 *
 * Le lien d'invitation n'ouvre plus de session : il n'a servi qu'une fois, à
 * établir ces identifiants. À chaque expiration de session — huit heures — on
 * repasse par ici.
 */
export function ConnexionClient() {
  const { session, connecterClient } = useSession();
  const naviguer = useNavigate();
  const lieu = useLocation();

  // Un avis pose par l'ecran precedent — « ce lien a deja servi », par exemple.
  // Ce n'est PAS une erreur : le distinguer visuellement evite de faire croire
  // a une panne a quelqu'un qui n'a qu'a se connecter normalement.
  const avis = (lieu.state as { avis?: string } | null)?.avis ?? null;

  const [identifiant, setIdentifiant] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await connecterClient(identifiant, motDePasse);
      naviguer("/portail", { replace: true });
    } catch (e) {
      // Le message vient du serveur et ne dit JAMAIS lequel des deux est faux,
      // ni si le dossier est fermé. On ne l'enrichit pas ici : le préciser
      // apprendrait à qui essaie des identifiants lesquels existent.
      setErreur(e instanceof ErreurApi ? e.message : "Connexion impossible.");
    } finally {
      setEnCours(false);
    }
  }

  // Deja connecte : cet ecran n'a rien a demander.
  if (session !== null && (session.role === "client" || session.role === "delegue"))
    return <Navigate to="/portail" replace />;

  return (
    <main className="connexion">
      <header className="marque">
        <div className="marque__monogramme">MDC</div>
        <div className="marque__nom">La Maison de la Conformité</div>
        <p className="marque__sous-titre">SARL · Portail client</p>
      </header>

      {avis && (
        <p className="resume" style={{ textAlign: "center", marginBottom: "1.2rem" }}>
          {avis}
        </p>
      )}

      <form onSubmit={soumettre}>
        <label>
          Identifiant
          <input
            value={identifiant}
            onChange={(e) => setIdentifiant(e.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label>
          Mot de passe
          <input
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {erreur && (
          <p className="alerte" role="alert">
            {erreur}
          </p>
        )}

        <button type="submit" disabled={enCours}>
          {enCours ? "Vérification…" : "Se connecter"}
        </button>
      </form>

      <p className="note" style={{ textAlign: "center", marginTop: "1rem" }}>
        Mot de passe oublié ? Votre gestionnaire MDC vous enverra un nouveau lien
        d'accès, qui vous permettra de refaire vos identifiants.
      </p>
    </main>
  );
}
